'use client';

/**
 * **TASK SUPPORT 구역의 큐레이션** — 담아 둔 작업(`menuJobs`)과 감춘 붙박이(`menuTasks`).
 *
 * ```
 *   CatchMomentsWorkspace/{id}.menuJobs    담아 둔 작업(고정한 것만 메뉴에 선다)
 *   CatchMomentsWorkspace/{id}.menuTasks   감춘 붙박이(기본은 다 켜짐)
 *     │ workspace.byId (실시간 구독 — 하나로 둘 다 읽는다)
 *   이 훅          ← 만들기·이름·고정·차례·지우기·붙박이 켜고 끄기
 *     ▼
 *   AI 작업 화면의 「내 작업」 구역 · 사이드바 TASK SUPPORT 의 고르개
 * ```
 *
 * 🔑 사이드바는 이 훅을 **안 쓴다** — 메뉴는 이미 실려 온 워크스페이스 문서에서 바로 읽는다
 *   (`nav-config-service`). 여기는 고치는 화면의 자리다.
 * ⚠️ **구독이다** — 다른 팀원이 작업을 고치면 여기도 따라 바뀐다.
 * ⚠️ 새 창구를 만들지 않았다 — 범용 `workspace.fields.update` 로 그 필드만 갈아 끼운다
 *   (내 화면(`menuSets`)과 같은 길이다).
 * 🚨 저장은 **목록 통째**다 — 한 줄만 보내면 나머지가 지워진다.
 */

import type { MenuJob } from 'src/utils/workspace/menu-jobs';

import { useState, useEffect, useCallback } from 'react';

import { useUser } from 'src/hooks/use-user';

import { parseHiddenTasks, toggleHiddenTask } from 'src/utils/workspace/menu-tasks';
import {
  addMenuJob,
  editMenuJob,
  moveMenuJob,
  MENU_JOB_MAX,
  parseMenuJobs,
  removeMenuJob,
} from 'src/utils/workspace/menu-jobs';

import { fsCall, fsSubscribe } from 'src/actions/firestore/dispatch';

export function useMenuJobs() {
  const user = useUser();
  const workspaceId = String(user?.lastWorkspace ?? '').trim();

  const [jobs, setJobs] = useState<MenuJob[]>([]);
  /** 감춘 붙박이 — 기본은 빈 목록(다 켜짐) */
  const [hiddenTasks, setHiddenTasks] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  /** 지금 저장 중인 작업 — 그 줄만 도는 표시를 낸다 */
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!workspaceId) {
      setJobs([]);
      setHiddenTasks([]);
      setReady(true);
      return undefined;
    }
    setReady(false);
    const unsub = fsSubscribe('workspace.byId', { workspaceId }, {
      onChange: (doc) => {
        const row = doc as Record<string, unknown> | null;
        setJobs(parseMenuJobs(row?.menuJobs));
        setHiddenTasks(parseHiddenTasks(row?.menuTasks));
        setReady(true);
      },
    });
    return () => {
      try {
        unsub?.();
      } catch {
        /* no-op */
      }
    };
  }, [workspaceId]);

  /**
   * 목록을 통째로 저장한다 — 화면은 응답을 안 기다리고 **먼저 바뀐다**(구독이 곧 같은 값을 실어 온다).
   * ⚠️ 실패하면 저장 전 목록으로 되돌린다 — 바뀐 것처럼 보이는 채로 두면 다음에 열었을 때 사라진다.
   */
  const persist = useCallback(
    async (next: MenuJob[], key: string): Promise<boolean> => {
      if (!workspaceId || saving) return false;
      const before = jobs;
      setSaving(key);
      setError('');
      setJobs(next);
      const res = await fsCall('workspace.fields.update', { workspaceId, payload: { menuJobs: next } });
      setSaving('');
      if (!res?.ok) {
        setJobs(before);
        setError(res?.error?.message || '저장하지 못했습니다.');
        return false;
      }
      return true;
    },
    [jobs, saving, workspaceId],
  );

  /** 바뀐 것이 없으면 저장하지 않는다 — 순수 함수가 «같은 배열»을 돌려주면 막힌 것이다 */
  const apply = useCallback(
    (next: MenuJob[], key: string, blocked: string): Promise<boolean> => {
      if (next === jobs) {
        setError(blocked);
        return Promise.resolve(false);
      }
      return persist(next, key);
    },
    [jobs, persist],
  );

  /** 지금 고른 조건을 내 작업으로 담는다 */
  const add = useCallback(
    (row: Omit<MenuJob, 'id'>) =>
      apply(addMenuJob(jobs, row), '', `내 작업은 ${MENU_JOB_MAX}개까지 담을 수 있습니다.`),
    [apply, jobs],
  );

  const edit = useCallback(
    (id: string, patch: Partial<MenuJob>) => persist(editMenuJob(jobs, id, patch), id),
    [jobs, persist],
  );

  /** 메뉴에 뽑나 — 라이브러리에서는 사라지지 않는다(머리말) */
  const togglePin = useCallback(
    (id: string) => {
      const row = jobs.find((one) => one.id === id);
      if (!row) return Promise.resolve(false);
      return persist(editMenuJob(jobs, id, { pinned: !row.pinned }), id);
    },
    [jobs, persist],
  );

  const move = useCallback(
    (id: string, delta: number) => apply(moveMenuJob(jobs, id, delta), id, '더 옮길 수 없습니다.'),
    [apply, jobs],
  );

  const remove = useCallback(
    (id: string) => apply(removeMenuJob(jobs, id), id, '그 작업을 찾지 못했습니다.'),
    [apply, jobs],
  );

  /**
   * 붙박이 하나를 켜고 끈다 — **지우는 것이 아니라 감추는 것**이다(`menu-tasks` 머리말).
   * ⚠️ 목록 통째 저장이라 저장 실패하면 되돌린다(작업 쪽과 같은 규약).
   */
  const toggleTask = useCallback(
    async (key: string): Promise<boolean> => {
      if (!workspaceId || saving) return false;
      const before = hiddenTasks;
      const next = toggleHiddenTask(hiddenTasks, key);
      if (next === hiddenTasks) return false;
      setSaving(key);
      setError('');
      setHiddenTasks(next);
      const res = await fsCall('workspace.fields.update', { workspaceId, payload: { menuTasks: next } });
      setSaving('');
      if (!res?.ok) {
        setHiddenTasks(before);
        setError(res?.error?.message || '저장하지 못했습니다.');
        return false;
      }
      return true;
    },
    [hiddenTasks, saving, workspaceId],
  );

  return {
    jobs,
    hiddenTasks,
    toggleTask,
    ready,
    saving,
    error,
    add,
    edit,
    togglePin,
    move,
    remove,
    full: jobs.length >= MENU_JOB_MAX,
  };
}
